import React, { useEffect, useState } from 'react';
import { useSearchParams, NavLink } from "react-router-dom";
import LoggedLayout from '../../layouts/LoggedLayout';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import PageHeader from 'components/PageHeader';
import Paginator from 'components/Paginator';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import Details from 'pages/Conteos/Details';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { default as ConteosService } from 'services/Conteos';

const Filters = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;

	.filters-inputs {
	    display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: nowrap;

		@media (max-width: 1200px) {
			& {
				display: flex !important;
				justify-content: flex-end;
				width: 100% !important;
			}
		}

		@media (min-width: 576px) and (max-width: 1200px) {
			& > *:nth-child(2) {
				min-width: 200px;
			}
		}

		@media (min-width: 768px) and (max-width: 991px) {
			#search-box {
				flex: 1;
			}
		}

		@media (max-width: 576px) {
			flex-direction: column;
			align-items: stretch;
			gap: 1rem;
			width: 100%;
			max-width: 100%;
			box-sizing: border-box;

			& > * {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}

			#search-box {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}
		}

		@media (max-width: 374px) {
			& {
				margin: auto;
			}
		}
	}

	.filter-buttons{
		width: auto !important;

		& > * {
			@media (min-width: 1201px) {
				height: 100%;
			}

			@media (max-width: 576px) {
				width: 100%;
				text-align: center;
			}
		}

		@media (max-width: 1200px) {
			margin-right: 0.5rem;
		}

		@media (max-width: 991px) {
			width: calc(100% - 1.5rem) !important;
		}

		@media (max-width: 767px) {
			width: calc(100% - 2rem);
		}

		@media (max-width: 576px) {
			display: flex !important;
			flex-direction: column !important;
			flex-wrap: nowrap;
			align-items: stretch !important;
			width: 100% !important;
		}
	}
	
	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;
		align-items: flex-end;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}

	@media (max-width: 991px) {
		display: grid;
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		gap: 10px;
		align-items: stretch;

		& > * {
			width: 100% !important;
			margin-bottom: 0 !important;
		}
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
	}
`;

const EmpoweredSelectorWrapper = styled.div` 
	margin-right: 10px;

	.empowered-selector {
		width: 200px;
		
		.empowered-selector_label {
			height: 28px;
			white-space: nowrap;
			overflow: hidden;

			&:hover {
				background: white;
			}

			.remove-icon {
				background: white;
				border-radius: 50%;
			}
		}
	}
`;

let axiosCancelToken = null;

export default function Conteos() {
	const [queryParams] = useSearchParams();

	let [forceReload, setForceReload] = useState(null);
	let [conteos, setConteos] = useState([]);
	let [sortDirection, setSortDirection] = useState('desc');
	let [sortField, setSortField] = useState('created_at');
	let [detailsOpened, setDetailsOpened] = useState([]);
	let [details, setDetails] = useState({});
	let [station, setStation] = useState(null);
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? undefined);
	const setPage = (page) => {
		setConteos({...conteos, data: undefined});
		_setPage(page);
	}

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const toggleDetails = (rowId) => {
		if ( detailsOpened.includes(rowId) ) {
			detailsOpened.splice(detailsOpened.indexOf(rowId), 1);
			setDetailsOpened([...detailsOpened]);

			// Remove details
			delete details[rowId];
			setDetails({...details});
		} else {
			setDetailsOpened([...detailsOpened, rowId]);
		}
	}

	const loadStations = (input, callback) => {
		axios.get('/api/stations/list', {
			params: {
				search: input,
				no_paginate: true
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: el.name
				};
			});

			callback(formatted);
	  	}).catch((error) => {			
			if ( axios.isCancel(error) ) return;
		});	
	}

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		let getConteos = async () => {
			let conteos = await ConteosService.list({
				page: page,
				sort: sortField,
				direction: sortDirection,
				station_id: station?.id
			});
			setSkeletonRows(conteos?.data?.length);
			setConteos({...conteos});
		}
		getConteos();

		let reloadInterval = setInterval(() => {
			getConteos();
		}, 5000);

		return function cleanup() {
			clearInterval(reloadInterval);
		}
	}, [forceReload, page, sortField, sortDirection, station]);

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader>
					<span className="header-column header-title">Conteos</span>
					<span className="header-column header-icon-end">
						<i className="bi bi-arrow-counterclockwise text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<div className="col-lg-2 align-content-center">
							Registro de conteos
						</div>
						<Filters className="col-lg-10 gap-1 gap-lg-2 filters-inputs">
							<EmpoweredSelectorWrapper>
								<EmpoweredSelector
									load={(input, callback) => loadStations(input, callback)}
									onChange={(value, text) => setStation(value)}
									timeout={200}
									label={station?.name}
									value={station?.id ?? null}
									placeholder="Estación"
								/>
							</EmpoweredSelectorWrapper>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
												<thead>
													<tr>
														<ThSortable className="title_conteos-estacion" direction={sortDirection} active={sortField === 'station.name'} onClick={() => sortTableClick('station.name')}>Estación</ThSortable>
														<th className="title_conteos-competencia">Competencia</th>
														<th className="title_conteos-fecha">Fecha</th>
														<ThSortable className="title_conteos-estado" direction={sortDirection} active={sortField === 'sent_at'} onClick={() => sortTableClick('sent_at')}>Estado</ThSortable>
													</tr>
												</thead>
												<tbody>
													{ conteos.data?.map((el, idx) => {
														return (
															<React.Fragment key={el.id}>
																<tr className={'has-details ' + (detailsOpened.includes(el.id) ? 'opened' : '')} onClick={() => toggleDetails(el.id)}>
																	<td className="title_conteos-estacion">
																		<div className="d-flex justify-content-between">
																			<span>{el.station?.name}</span>
																		</div>
																	</td>
																	<td className="title_conteos-competencia">
																		{el.data?.competitor?.name ?? ''}
																	</td>
																	<td className="title_conteos-fecha">
																		{el.data?.date_execution ? moment(el.data?.date_execution).format('DD-MM-YYYY') : ''}
																	</td>
																	<td className="title_conteos-estado">
																		{el.sent_at ? <span className="badge bg-plenoil-green">enviado</span> : <span className="badge bg-plenoil-red">no enviado</span>}
																	</td>
																</tr>
																{ detailsOpened.includes(el.id) &&
																	<Details 
																		conteo={el}
																	/>
																}
															</React.Fragment>
														)
													}) }

													{ conteos.data && !conteos.data.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

													{ conteos.data === undefined && <TrSkeleton rows={skeletonRows} columns={4} /> }
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-md-12 text-center mb-3">
				<div className="d-inline-block">
					<Paginator
						min={1}
						current={conteos?.current_page}
						max={conteos?.last_page}
						changeCallback={(page) => setPage(page)}
					/>
				</div>
			</div>
		</LoggedLayout>
	);
}